import React, { useMemo } from 'react';
import ViewMethodology, { type MethodologySection } from '../../../molecules/ViewMethodology/ViewMethodology';
import { serializeMethodologyForPptExport } from '../../../../utils/artifactExport';

export interface MethodologyWithExportCaptureProps {
    title?: string;
    sections: MethodologySection[];
    formula?: string | string[];
    defaultExpanded?: boolean;
    onExpandChange?: (expanded: boolean) => void;
    'data-test-id'?: string;
}

/**
 * Live collapsible methodology for the panel plus an off-screen expanded clone
 * captured for PDF/DOCX so exports match in-app styling (card, bullets, formula box).
 */
const MethodologyWithExportCapture: React.FC<MethodologyWithExportCaptureProps> = ({
    title,
    sections,
    formula,
    defaultExpanded = false,
    onExpandChange,
    'data-test-id': dataTestId,
}) => {
    const serialized = useMemo(
        () => serializeMethodologyForPptExport({ title, sections, formula }),
        [title, sections, formula],
    );

    return (
        <div className="relative block w-full">
            <div data-export-exclude data-export-exclude-ppt className="block w-full">
                <ViewMethodology
                    title={title}
                    sections={sections}
                    formula={formula}
                    defaultExpanded={defaultExpanded}
                    onExpandChange={onExpandChange}
                    data-test-id={dataTestId}
                />
            </div>
            {/* Zero-size clip: expanded export clone must not inflate right-panel scroll height. */}
            <div className="absolute top-0 left-0 w-0 h-0 overflow-hidden" aria-hidden="true">
                <div
                    data-export-methodology-capture
                    data-export-methodology={serialized}
                    className="w-[840px] pointer-events-none"
                >
                    <ViewMethodology
                        forExport
                        title={title}
                        sections={sections}
                        formula={formula}
                        data-test-id={dataTestId ? `${dataTestId}-export-capture` : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

export default MethodologyWithExportCapture;
